/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { formatNameFirstLast, sortAsc } from '@strata/helper-functions';

import { OOPS_API_URL } from 'api/APIBase';
import { type MemberType } from 'services/constants';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = { INCIDENT: 'INCIDENT' } as const;

const sortNameAsc = (a: { name: string }, b: { name: string }) => sortAsc(a.name, b.name);

export interface Member {
    memberId: string;
    firstName: string;
    lastName?: string;
    dateOfBirth: string;
    benefitCode?: string;
    benefitGroup?: string;
    relationshipCode?: string;
    name: string;
    secondaryData: string;
}

export interface ValueCase {
    value_case_id: string;
    value_case_name: string;
    value_case_description: string;
    performance_days: number;
    evaluation_days: number;
    strat_rule_id: string;
    strat_rule_display_name: string;
    performance_requirement_name: string;
    payer_id: string;
    segment_id: string;
    name: string;
    diagnoses: { dx_oid: string; value_case_id: string }[];
    product_service_incentives: {
        value_case_id: string;
        product_service_oid: string;
        incentive_amt: number;
    }[];
}

export const oopsApi = createApi({
    baseQuery: getBaseQuery(OOPS_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'oopsApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getDiagnosisList: build.query<
            {
                name: string;
                oid: string;
                codes: {
                    code: string;
                    description: string;
                    oid: string;
                }[];
            }[],
            void
        >({
            query: () => 'datasets/diagnoses',
            transformResponse: (
                response: {
                    oid: string;
                    description: string;
                    codes: { code: string; description: string; oid: string }[];
                }[],
            ) =>
                response
                    ?.map((diagnosis) => ({
                        name: diagnosis.description,
                        oid: diagnosis.oid,
                        codes: diagnosis.codes,
                    }))
                    .sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching diagnosis list.',
            ),
        }),
        getProductList: build.query<
            {
                oid: string;
                description: string;
                genericDescription: string;
                brandDescription: string;
                isBrandDefault: boolean;
                productCode?: string;
                isActive: boolean;
                name: string;
                secondaryData?: string;
            }[],
            { segmentId: string; benefit: string; includeInactive?: boolean }
        >({
            query: ({ segmentId, benefit, includeInactive = false }) => ({
                url: `datasets/products/benefit/list`,
                params: {
                    segment_id: segmentId,
                    benefit,
                    include_inactive: includeInactive,
                },
            }),
            transformResponse: (
                response: {
                    oid: string;
                    description: string;
                    generic_product_description: string;
                    brand_product_description: string;
                    is_brand_default: boolean;
                    product_code?: string;
                    is_active?: boolean;
                }[],
            ) =>
                response
                    ?.map((product) => ({
                        oid: product.oid,
                        description: product.description,
                        genericDescription: product.generic_product_description,
                        brandDescription: product.brand_product_description,
                        isBrandDefault: product.is_brand_default,
                        productCode: product.product_code,
                        isActive: product.is_active ?? true,
                        name: product.oid,
                        secondaryData: product.product_code,
                    }))
                    .sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching products.'),
        }),
        getMemberList: build.query<Member[], { payerId: string; memberType: MemberType }>({
            query: ({ payerId, memberType }) => `members/all/${payerId}/${memberType}`,
            transformResponse: (
                response: {
                    member_id: string;
                    first_name: string;
                    last_name?: string;
                    date_of_birth: string;
                    benefit_code?: string;
                    benefit_group?: string;
                    relationship_code?: string;
                }[],
            ) =>
                response
                    ?.map((member) => ({
                        memberId: member.member_id,
                        firstName: member.first_name,
                        lastName: member.last_name,
                        dateOfBirth: member.date_of_birth,
                        benefitCode: member.benefit_code,
                        benefitGroup: member.benefit_group,
                        relationshipCode: member.relationship_code,
                        name: member.member_id,
                        secondaryData: `${formatNameFirstLast(member.first_name, member.last_name)} | ${member.date_of_birth}`,
                    }))
                    .sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching members.'),
        }),
        getValueCaseList: build.query<ValueCase[], void>({
            query: () => '/datasets/value_cases',
            transformResponse: (response: Omit<ValueCase, 'name'>[]) =>
                response?.map((vc) => ({ ...vc, name: vc.value_case_name })).sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching value cases.'),
        }),
        getTopicsAndSubscriptions: build.query<
            {
                name: string;
                activeCount: number;
                deadCount: number;
                subscriptions: {
                    name: string;
                    activeCount: number;
                    deadCount: number;
                }[];
            }[],
            void
        >({
            query: () => 'topics',
            transformResponse: (
                response: {
                    name: string;
                    active_message_count: number;
                    dead_letter_message_count: number;
                    subscriptions: {
                        name: string;
                        active_message_count: number;
                        dead_letter_message_count: number;
                    }[];
                }[],
            ) =>
                response?.map((topic) => ({
                    name: topic.name,
                    activeCount: topic.active_message_count,
                    deadCount: topic.dead_letter_message_count,
                    secondaryData: `Active: ${topic.active_message_count} | DeadLetter: ${topic.dead_letter_message_count}`,
                    subscriptions: topic.subscriptions.map((subscription) => ({
                        name: subscription.name,
                        activeCount: subscription.active_message_count,
                        deadCount: subscription.dead_letter_message_count,
                        secondaryData: `Active: ${subscription.active_message_count} | DeadLetter: ${subscription.dead_letter_message_count}`,
                    })),
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching topics and subscriptions.',
            ),
        }),
        getActiveAuctions: build.query<
            { referral_id: string; name: string; secondaryData: string }[],
            void
        >({
            query: () => `/auctions/all`,
            transformResponse: (
                response: {
                    referral_id: string;
                    segment_id: string;
                    product_service_oid: string;
                    product_service_description: string;
                }[],
            ) =>
                response
                    ?.map((referral) => ({
                        referral_id: referral.referral_id,
                        name: `${referral.referral_id} - ${referral.segment_id} - ${referral.product_service_oid}`,
                        secondaryData: referral.product_service_description,
                    }))
                    .sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching auctions.'),
        }),
        getMissingQualityChecks: build.query<string[], void>({
            query: () => 'incident-tracking/incidents/missing-quality-checks',
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching missing quality checks.',
            ),
        }),
        getImpactedServiceNames: build.query<string[], void>({
            query: () => 'incident-tracking/incidents/impacted-service-names',
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching impacted service names.',
            ),
        }),
        getSeverityLevels: build.query<
            {
                id: number;
                name: string;
                description: string;
                user_impact: string;
                business_impact: string;
                infrastructure: string;
                security: string;
            }[],
            void
        >({
            query: () => 'incident-tracking/incidents/severity-levels',
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching severity levels.',
            ),
        }),
        getIncidents: build.query<
            {
                id: string;
                name: string;
                startedAt: string;
                description: string;
                missingQualityCheck: string;
                primaryImpactedServiceName: string;
                resolvedAt?: string;
                severity?: number;
            }[],
            void
        >({
            query: () => 'incident-tracking/incidents',
            transformResponse: (
                response: {
                    id: string;
                    name: string;
                    started_at: string;
                    description: string;
                    missing_quality_check: string;
                    primary_impacted_service_name: string;
                    resolved_at?: string;
                    severity?: number;
                }[],
            ) =>
                response?.map((incident) => ({
                    id: incident.id,
                    name: incident.name,
                    startedAt: incident.started_at,
                    description: incident.description,
                    missingQualityCheck: incident.missing_quality_check,
                    primaryImpactedServiceName: incident.primary_impacted_service_name,
                    resolvedAt: incident.resolved_at,
                    severity: incident.severity,
                })) ?? [],
            transformErrorResponse: handleRTKQueryError('An error occurred fetching incidents.'),
        }),
        resolveIncident: build.mutation<
            void,
            { incidentId: string; resolvedAt: string; severity: number }
        >({
            query: ({ incidentId, resolvedAt, severity }) => ({
                url: `incident-tracking/incidents/${incidentId}/resolve`,
                method: 'POST',
                body: { resolved_at: resolvedAt, severity },
            }),
            invalidatesTags: (_result, _error, { incidentId }) => [
                { type: TagType.INCIDENT, id: incidentId },
            ],
        }),
        editIncident: build.mutation<
            void,
            {
                incidentId: string;
                name?: string;
                started_at?: string;
                description?: string;
                missing_quality_check?: string;
                primary_impacted_service_name?: string;
                severity?: number;
            }
        >({
            query: ({ incidentId, ...body }) => ({
                url: `incident-tracking/incidents/${incidentId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { incidentId }) => [
                { type: TagType.INCIDENT, id: incidentId },
            ],
        }),
        getIncident: build.query<
            {
                id: string;
                name: string;
                startedAt: string;
                description: string;
                missingQualityCheck: string;
                primaryImpactedServiceName: string;
                resolvedAt?: string;
                severity?: number;
            },
            string
        >({
            query: (incidentId) => `incident-tracking/incidents/${incidentId}`,
            transformResponse: (response: {
                id: string;
                name: string;
                started_at: string;
                description: string;
                missing_quality_check: string;
                primary_impacted_service_name: string;
                resolved_at?: string;
                severity?: number;
            }) => ({
                id: response.id,
                name: response.name,
                startedAt: response.started_at,
                description: response.description,
                missingQualityCheck: response.missing_quality_check,
                primaryImpactedServiceName: response.primary_impacted_service_name,
                resolvedAt: response.resolved_at,
                severity: response.severity,
            }),
            providesTags: (_result, _error, incidentId) => [
                { type: TagType.INCIDENT, id: incidentId },
            ],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching incident details.',
            ),
        }),
        getActiveReferrals: build.query<
            {
                payerId: string;
                memberId: string;
                referralId: string;
                productName: string;
                name: string;
                secondaryData: string;
            }[],
            { supplierNpi: string }
        >({
            query: ({ supplierNpi }) => `worklist_tasks/referrals/${supplierNpi}`,
            transformResponse: (
                response: {
                    payer_id: string;
                    member_id: string;
                    referral_id: string;
                    product_name: string;
                }[],
            ) =>
                response
                    ?.map((referral) => ({
                        payerId: referral.payer_id,
                        memberId: referral.member_id,
                        referralId: referral.referral_id,
                        productName: referral.product_name,
                        name: referral.member_id,
                        secondaryData: `${referral.product_name} | ${referral.referral_id}`,
                    }))
                    .sort(sortNameAsc),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching referrals.'),
        }),
    }),
});

export const {
    useEditIncidentMutation,
    useGetActiveAuctionsQuery,
    useGetIncidentQuery,
    useGetIncidentsQuery,
    useResolveIncidentMutation,
    useGetMissingQualityChecksQuery,
    useGetImpactedServiceNamesQuery,
    useGetSeverityLevelsQuery,
    useLazyGetActiveReferralsQuery,
    useGetDiagnosisListQuery,
    useLazyGetProductListQuery,
    useLazyGetMemberListQuery,
    useGetValueCaseListQuery,
    useGetTopicsAndSubscriptionsQuery,
} = oopsApi;
